<template>
  <vl-region>
    <vl-layout>
      <PbsGoBackLink />
      <template v-if="!loading">
        <vl-grid>
          <vl-column width="6" width-xs="12">
            <vl-title tag-name="h1"> {{ $t('processtrigger.detail.title') }} </vl-title>
          </vl-column>
          <vl-column width="6" width-xs="12">
            <vl-action-group
              v-if="detail && !editStore.isEdit(ProcessTriggerEditType.ProcessTrigger, detail.id)"
              mod-align-right
              mod-space
              between
            >
              <vl-button
                icon="pencil"
                mod-icon-before
                mod-secondary
                :mod-disabled="editStore.editing"
                @click="editStore.openEdit(ProcessTriggerEditType.ProcessTrigger, detail.id)"
              >
                {{ $t('general.edit') }}
              </vl-button>
              <ProcessTriggerActionButton :detail="detail" :actions="actionButtonActions" mod-large />
              <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal">{{
                $t('general.delete')
              }}</vl-button>
            </vl-action-group>
            <vl-action-group v-else mod-align-right mod-space between>
              <vl-button icon="save" :mod-loading="reloading" mod-icon-before @click="save">{{
                $t('general.save')
              }}</vl-button>
              <vl-button
                icon="cross"
                :mod-loading="loading"
                mod-icon-before
                mod-secondary
                mod-error
                @click="editStore.closeEdit"
              >
                {{ $t('general.cancel') }}
              </vl-button>
            </vl-action-group>
          </vl-column>
        </vl-grid>
        <vl-grid v-if="showRunning" class="vl-u-spacer-bottom--medium">
          <vl-column>
            <vl-alert
              icon="warning"
              :title="$t('processtrigger.detail.running.title')"
              :content="runningMessage"
              mod-warning
            />
          </vl-column>
        </vl-grid>
        <vl-grid
          v-if="detail"
          :class="{
            edit: editStore.isEdit(ProcessTriggerEditType.ProcessTrigger, detail.id),
            'vl-u-spacer-bottom--medium': true
          }"
        >
          <vl-column>
            <ProcessTriggerRead v-if="!editStore.isEdit(ProcessTriggerEditType.ProcessTrigger, detail.id)" />
            <ProcessTriggerEdit v-else ref="editForm" />
          </vl-column>
        </vl-grid>
      </template>
      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('processtrigger.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { useProcessTriggerDetailStore } from '../../store/process-trigger/process-trigger-detail.store';
import {
  useProcessTriggerEditStore,
  ProcessTriggerEditType
} from '../../store/process-trigger/process-trigger-edit.store';
import { ProcessTriggerDetailDto } from '@/api/backoffice/clients';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import ProcessTriggerRead from './ProcessTriggerRead.vue';
import ProcessTriggerEdit from './ProcessTriggerEdit.vue';
import ProcessTriggerActionButton from './subcomponents/process-trigger-action-button/ProcessTriggerActionButton.vue';
import { ProcessTriggerActionButtonType } from '../../common/process-trigger.common';
import { useModalStore } from '@/modules/core/store/modal.store';
import { ENUM_TriggerTypes } from '../../common/configuration-enumerations.common';
import { defaultDatetimeFormatter } from '@/filters';

const store = useProcessTriggerDetailStore();
const editStore = useProcessTriggerEditStore();
const modalStore = useModalStore();
const { t } = useI18n();

const route = useRoute();

const editForm = ref();

const detail = computed((): ProcessTriggerDetailDto => {
  return store.detail;
});

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const actionButtonActions = [ProcessTriggerActionButtonType.Execute];

// A run is started by putting a message on the bus, so nothing tells the screen when it is done. Asking
// every few seconds is enough to follow along without leaning on the server.
const statusPollIntervalMs = 5000;
let statusPoller: ReturnType<typeof setInterval> | null = null;

// Without a start time there is only half a sentence to show, so the banner stays away altogether.
const showRunning = computed((): boolean => {
  return store.status?.isRunning === true && !!store.status?.startedOn;
});

// What is busy differs per kind of trigger: for a process definition it is a delivery or a product being
// processed, which is what the user cares about, rather than the trigger that happened to start it.
const runningMessage = computed((): string => {
  const startedOn = store.status?.startedOn;
  const values = { startedOn: startedOn ? defaultDatetimeFormatter(startedOn) : '' };

  if (detail.value?.deliveryProcessDefinition) {
    return t('processtrigger.detail.running.delivery', {
      ...values,
      definition: detail.value.deliveryProcessDefinition.label
    });
  }

  if (detail.value?.productProcessDefinition) {
    return t('processtrigger.detail.running.product', {
      ...values,
      definition: detail.value.productProcessDefinition.label
    });
  }

  return t('processtrigger.detail.running.trigger', values);
});

const stopPollingStatus = () => {
  if (statusPoller) {
    clearInterval(statusPoller);
    statusPoller = null;
  }
};

const startPollingStatus = () => {
  // Only a time trigger runs on its own; the other types are started by the process they hang off.
  if (detail.value?.triggerType?.id !== ENUM_TriggerTypes.TijdTrigger.id) {
    return;
  }

  store.getStatus(id.value);
  statusPoller = setInterval(() => store.getStatus(id.value), statusPollIntervalMs);
};

onMounted(async () => {
  await store.get(id.value);
  startPollingStatus();
});

onUnmounted(stopPollingStatus);

onBeforeRouteLeave((to, from, next) => {
  const id = parseInt(from.params.id as string, 10);
  if (id && store) {
    store.clear(id);
    editStore.closeEdit();
  }
  next();
});

const save = () => {
  editForm.value.onSubmit();
};

const showDeleteModal = () => {
  modalStore.showDeleteModal({
    title: t('processtrigger.detail.delete.title'),
    props: {
      navigationRouteOnSuccess: { name: 'configuration', hash: '#processtrigger' },
      confirm: () => store.delete(id.value)
    }
  });
};

onBeforeRouteLeave((to, from, next) => {
  const id = parseInt(from.params.id as string, 10);
  if (id && store) {
    store.clear(id);
  }
  next();
});
</script>
<style lang="scss" scoped>
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colorsRaw.scss';
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colors.scss';

.pbs-form-group {
  background: $vl-alt-bg;
  padding: 3rem;
}
</style>
